import { getPermittedFrontendAddons } from "@/api/frontendAddons";
import { LoadingCover } from "@/components/LoadingCover";
import { ViewType } from "@/domain/ViewType";
import { translation } from "@/i18n";
import { nsApp } from "@/i18n/locales/app";
import loadable from "@loadable/component";
import React, {
  createContext,
  ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useMatch } from "react-router";
import { useAppAccount } from "../AppAccountProvider";
import {
  SpreadsheetUIFrontendAddonManagerContextProvider,
  useSpreadsheetUIFrontendAddonManager,
} from "./adapter";

const FRONTEND_ADDON_BUCKET_ID = (() => {
  if (process.env.FRONTEND_ADDON_BUCKET_ID)
    return process.env.FRONTEND_ADDON_BUCKET_ID;
  switch (process.env.APP_ENV) {
    case "qa":
      return "aa-sheets-ui-qa-gcp-frontend-addon";
    case "staging":
      return "aa-sheets-ui-stg-gcp-frontend-addon";
    case "production":
      return "aa-sheets-ui-prod-gcp-frontend-addon";
    default:
      return "aa-sheets-ui-dev-gcp-frontend-addon";
  }
})();

const getFrontendAddonBaseUrl = (pathPrefix: string) =>
  `https://storage.googleapis.com/${FRONTEND_ADDON_BUCKET_ID}/${pathPrefix}`;

const loadAddonQueue: {
  [key: string]: {
    pathPrefix: string;
    job: Promise<void>;
  };
} = {};
const loadAddon = ({ id, pathPrefix }: { id: string; pathPrefix: string }) => {
  if (loadAddonQueue[id] && loadAddonQueue[id].pathPrefix === pathPrefix) {
    return loadAddonQueue[id].job;
  }
  const job = new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.addEventListener("load", () => {
      document.head.removeChild(script);
      resolve();
    });
    script.async = true;
    script.src = getFrontendAddonBaseUrl(pathPrefix) + "/index.js";
    document.head.appendChild(script);
  });
  loadAddonQueue[id] = {
    pathPrefix,
    job,
  };
  return job;
};

const ctx = createContext<{
  addons: ReturnType<typeof useSpreadsheetUIFrontendAddonManager>["addons"];
  viewTypes: ViewType<any, any>[];
  activeAddonIds: string[];
  isAllActiveAddonsLoaded: boolean;
  ignoreLoadedCheck: boolean;
  setIgnoreLoadedCheck: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  addons: {},
  viewTypes: [],
  activeAddonIds: [],
  isAllActiveAddonsLoaded: false,
  ignoreLoadedCheck: false,
  setIgnoreLoadedCheck: () => {
    // nop
  },
});

const AppFrontendAddonProviderDeveloperTools = loadable(() =>
  import("./AppFrontendAddonProviderDeveloperTools").then(
    (c) => c.AppFrontendAddonProviderDeveloperTools
  )
);

const useTranslation = translation.use(nsApp);

const AppFrontendAddonProviderBase: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const params = useMatch("/organizations/:organizationId/*")?.params;
  const organizationId = params?.organizationId ?? null;
  const organizationChildPath = params?.["*"] ?? null;

  const [[activeAddonIds, loadedAddonIdsOrgId], setActiveAddonIdsState] =
    useState<[string[], string | null]>([[], null]);
  useLayoutEffect(() => {
    let _setActiveAddonIdsState = setActiveAddonIdsState;
    if (organizationId) {
      (async () => {
        const addons = await getPermittedFrontendAddons({ organizationId });
        _setActiveAddonIdsState([
          addons.map((addon) => addon.id),
          organizationId,
        ]);
        await Promise.all(addons.map((addon) => loadAddon(addon)));
      })();
    } else {
      _setActiveAddonIdsState([[], organizationId]);
    }
    return () => {
      _setActiveAddonIdsState([[], null]);
      _setActiveAddonIdsState = () => {
        // nop
      };
    };
  }, [organizationId]);
  const isAddonInfoLoaded = loadedAddonIdsOrgId === organizationId;
  const loadedAddons = useSpreadsheetUIFrontendAddonManager().addons;
  const loadedActiveAddons = useMemo(
    () =>
      Object.values(loadedAddons).filter((addon) =>
        activeAddonIds.includes(addon.id)
      ),
    [loadedAddons, activeAddonIds]
  );
  const [ignoreLoadedCheck, setIgnoreLoadedCheck] = useState(false);
  const isAllActiveAddonsLoaded =
    (loadedActiveAddons.length === activeAddonIds.length &&
      loadedActiveAddons.every((addon) => addon.initialized)) ||
    ignoreLoadedCheck;

  const ctxValue = useMemo(
    () => ({
      addons: loadedAddons,
      viewTypes: loadedActiveAddons.reduce(
        (acc, addon) => [...acc, ...addon.viewTypes],
        [] as ViewType<any, any>[]
      ),
      activeAddonIds,
      isAllActiveAddonsLoaded,
      ignoreLoadedCheck,
      setIgnoreLoadedCheck,
    }),
    [
      loadedAddons,
      loadedActiveAddons,
      activeAddonIds,
      isAllActiveAddonsLoaded,
      ignoreLoadedCheck,
    ]
  );
  const { isAADeveloperOrAbove } = useAppAccount();

  return (
    <ctx.Provider value={ctxValue}>
      {(isAllActiveAddonsLoaded && isAddonInfoLoaded) ||
      (isAADeveloperOrAbove &&
        organizationChildPath?.startsWith("frontend_addon")) ? (
        children
      ) : (
        <div style={{ height: "100dvh" }}>
          <LoadingCover loadingText={t.app.loading.frontendAddon()} />
          {isAADeveloperOrAbove ? (
            <AppFrontendAddonProviderDeveloperTools />
          ) : null}
        </div>
      )}
    </ctx.Provider>
  );
};

export const AppFrontendAddonProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  return (
    <SpreadsheetUIFrontendAddonManagerContextProvider>
      <AppFrontendAddonProviderBase>{children}</AppFrontendAddonProviderBase>
    </SpreadsheetUIFrontendAddonManagerContextProvider>
  );
};

export const useActiveFrontendAddons = () => useContext(ctx);
